import { useRouter } from 'next/router';
import { useAuth } from '@/hooks';
import { clearAuthFromStorage } from '@/utils/auth';
import { clearBugsnagUser } from '@/utils/bugsnag';

/**
 * Centralized hook for redirecting to login with proper cleanup
 * Replaces all scattered login redirect methods throughout the app
 */
export function useRedirectToLogin() {
  const router = useRouter();
  const { setAuthData } = useAuth();

  const redirectToLogin = () => {
    // 1. Clean up localStorage
    clearAuthFromStorage();

    // 2. Clean up Bugsnag user
    clearBugsnagUser();

    // 3. Clean up React state
    setAuthData(undefined);

    // 4. Redirect using Next.js router (client-side navigation)
    router.push('/login');
  };

  return redirectToLogin;
}

/**
 * Hook for redirecting to dashboard based on user type
 * Also handles cleanup if needed
 */
export function useRedirectToDashboard() {
  const router = useRouter();
  const { authData } = useAuth();
  const redirectToLogin = useRedirectToLogin();

  const redirectToDashboard = () => {
    if (!authData?.user) {
      // If no user data, redirect to login instead
      redirectToLogin();
      return;
    }

    const dashboardRoute = authData.user.is_client
      ? '/connect/campaigns'
      : '/connect/collaborations';

    router.push(dashboardRoute);
  };

  return redirectToDashboard;
}

/**
 * Hook for logout with proper cleanup and redirect
 * Replaces the current logout function
 */
export function useLogout() {
  const redirectToLogin = useRedirectToLogin();

  const logout = () => {
    // Clean up and redirect to login
    redirectToLogin();
  };

  return logout;
}
